import StatusBadge from "@components/statusbadge";
import { Link, useParams } from "react-router-dom";
import menuService from "@services/menuService";
import * as jobStatus from "@cores/jobStatus";
import MyJobProgressCard from "@components/myjob";
import React, { useCallback, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import JobEmptyList from "@routes/rightsidebar/ui/job-empty-list";
import { Box, Button, CircularProgress, Collapse, Divider, Grid, IconButton, Stack, Tooltip } from "@mzc-pdc/ui";
import ObjectIcon from "@routes/sources/components/ObjectIcon";
import Typography from "@components_v2/typography/typography";
import {
    IconArrowDown,
    IconArrowUp,
    IconCancelCircle,
    IconCircle,
    IconDownload,
    IconErrorCircle,
    IconHierarchyFolded,
    IconOpenInNew,
    IconProgress,
    IconRetry,
    IconTranscode,
} from "@mzc-cloudplex/icons";
import theme from "@styles/theme";
import State from "@components/state";
import moment from "moment/moment";
import { alpha } from "@mui/material";
import StorageDownloadCdnTooltip from "@components/tooltip/StorageDownloadCdnTooltip";
import convertToByte from "@cores/convertToByte";
import { useDispatch, useSelector } from "react-redux";
import { cancelTranscode } from "@modules/upload";
import { JOB_ID_LCOAL_STORAGE_KEY } from "@constants";
import { GridNameEllipsis } from "@components_v2/grid/grid.styled";

const TranscodeJobList = ({ jobs }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const [isExpanded, setIsExpanded] = useState({});

    const stage = useSelector(({ stage }) => stage);
    const project = useSelector(({ project }) => project);

    const onClickCancel = async (id) => {
        try {
            await dispatch(cancelTranscode(id));
            const storedTranscodeJobs = localStorage.getItem(JOB_ID_LCOAL_STORAGE_KEY).split(",");
            const newTranscodeJobs = storedTranscodeJobs.filter((jobId) => jobId !== id);
            localStorage.setItem(JOB_ID_LCOAL_STORAGE_KEY, newTranscodeJobs.join(","));
        } catch (e) {
            console.log(e);
        }
    };

    //Note: 부분 완료에 대한 상태가 가능한가?
    const renderJobStatus = useCallback((job) => {
        let color = "",
            text = "",
            useProgress = false;
        switch (job.status) {
            case jobStatus.values.READY:
                color = "warning.main";
                text = t(`common::label::READY`, `Ready`);
                useProgress = true;
                break;
            case jobStatus.values.INGESTING:
                color = "warning.main";
                text = t(`common::label::INGESTING`, `Ingesting`);
                useProgress = true;
                break;
            case jobStatus.values.INGESTED:
                color = "warning.main";
                text = t(`common::label::INGESTED`, `Ingested`);
                useProgress = true;
                break;
            case jobStatus.values.SUBMITTED:
                useProgress = true;
                color = "warning.main";
                text = t(`common::label::Submitted`, `Submitted`);
                break;
            case jobStatus.values.PROGRESSING:
                color = "primary.main";
                text = t(`common::label::Transcode in progress`, `Transcode in progress`);
                useProgress = true;
                break;
            case jobStatus.values.ERROR:
                color = "error.main";
                text = t(`common::label::An error occurred while transcoding.`, `An error occurred while transcoding.`);
                break;
            case jobStatus.values.CANCELED:
                text = t(`common::label::Transcode has been cancelled.`, `Transcode has been cancelled.`);
                break;
            case jobStatus.values.COMPLETE:
                color = "success.main";
                text = t(`common::label::Transcode completed.`, `Transcode completed.`);
                break;
        }
        return (
            <React.Fragment>
                <Typography variant="body3_400" color={color ?? "greySecondary"}>
                    {!!useProgress && (
                        <CircularProgress
                            color={color.split(".")[0]}
                            size={16}
                            thickness={5}
                            sx={{ mr: theme.spacing(1) }}
                        />
                    )}
                    <span>{`${text}`}</span>
                </Typography>
            </React.Fragment>
        );
    }, []);

    const toggleJobIsExpanded = useCallback(
        (job) => {
            setIsExpanded((prev) => ({ ...prev, [job.id]: !prev[job.id] }));
        },
        [isExpanded],
    );

    return (
        <Grid container pt={3} px={3} pb={2} gap={1} justifyContent={"center"} alignItems={"center"}>
            <Stack flex={1} height={1} gap={4}>
                {jobs.length > 0 ? (
                    <>
                        {jobs.map((item, i) => (
                            <Box>
                                <Grid container pb={1} gap={1} alignItems={"center"}>
                                    <Grid item>
                                        <Grid
                                            container
                                            alignItems={"center"}
                                            justifyContent={"center"}
                                            width={24}
                                            height={20}
                                            bgcolor={"greySecondary.main"}
                                            color={"greySecondary.contrastText"}
                                            borderRadius={0.7}
                                        >
                                            <IconTranscode size={16} />
                                        </Grid>
                                    </Grid>
                                    <Divider flexItem orientation={"vertical"} sx={{ my: 1 }} />
                                    <Grid
                                        item
                                        xs
                                        sx={{
                                            display: "flex",
                                            flexDirection: "row",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                        }}
                                    >
                                        <Grid item gap={1} alignItems={"center"}>
                                            <State variant={"contained"} status={item.status} />
                                            {(item.status === "FAILED" || item.partialFailure) && (
                                                <Tooltip
                                                    title={
                                                        item.summary?.exitCode !== "SUCCESS"
                                                            ? t(
                                                                  `common::code::${item.summary.exitCode}`,
                                                                  item.summary.exitCode,
                                                              )
                                                            : t(
                                                                  `common::label::This operation was not entirely successful.`,
                                                                  "This operation was not entirely successful.",
                                                              )
                                                    }
                                                >
                                                    <IconButton circled size="small">
                                                        <IconErrorCircle size={16} color="error" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Grid>
                                        <Grid item>
                                            {!jobStatus.isComplete(item.status) && (
                                                <Button
                                                    size={"small"}
                                                    color={"greySecondary"}
                                                    startIcon={<IconCancelCircle size={16} />}
                                                    onClick={() => onClickCancel(item.id)}
                                                >
                                                    {t(`common::label::Cancel`, `Cancel`)}
                                                </Button>
                                            )}
                                        </Grid>
                                    </Grid>
                                    <Grid item>
                                        {(item.status === "FAILED" || item.partialFailure) && (
                                            <Button
                                                variant={"text"}
                                                size={"small"}
                                                color={"greySecondary"}
                                                startIcon={<IconRetry size={16} />}
                                                onClick={() => onRetry(item)}
                                            >
                                                {t(`common::label::Retry`, `Retry`)}
                                            </Button>
                                        )}
                                    </Grid>
                                </Grid>
                                <Divider />
                                <Stack py={2.5} px={1.5} gap={1.5}>
                                    <Grid container gap={1}>
                                        <Grid item xs={3}>
                                            <Grid container gap={1} alignItems={"center"}>
                                                <IconCircle size={6} htmlColor={theme.palette.greySecondary[500]} />
                                                {t(`common::label::Job`, `Job`)}
                                            </Grid>
                                        </Grid>
                                        <Grid item>{t(`common::label::Transcode`, `Transcode`)}</Grid>
                                    </Grid>
                                    <Grid container gap={1}>
                                        <Grid item xs={3}>
                                            <Grid container gap={1} alignItems={"center"}>
                                                <IconCircle size={6} htmlColor={theme.palette.greySecondary[500]} />
                                                {t(`common::label::Job ID`, `Job ID`)}
                                            </Grid>
                                        </Grid>
                                        <Grid item>
                                            {["SUBMITTED", "RUNNING"].includes(item.status) ? (
                                                item.id ?? "-"
                                            ) : (
                                                <Link
                                                    href="#"
                                                    to={menuService.paths.jobDetail(stage.id, project.id, item.id)}
                                                    underline="hover"
                                                >
                                                    {item.id ?? "-"}
                                                    <IconOpenInNew size={16} />
                                                </Link>
                                            )}
                                        </Grid>
                                    </Grid>
                                    {item.createdAt && (
                                        <Grid container gap={1}>
                                            <Grid item xs={3}>
                                                <Grid container gap={1} alignItems={"center"}>
                                                    <IconCircle size={6} htmlColor={theme.palette.greySecondary[500]} />
                                                    {t(`common::label::Start date`, `Start date`)}
                                                </Grid>
                                            </Grid>
                                            <Grid item>{`${moment(item.createdAt)
                                                .add(9, "h")
                                                .format("YYYY-MM-DD HH:mm:ss")}`}</Grid>
                                        </Grid>
                                    )}
                                    {item.completedAt && (
                                        <Grid container gap={1}>
                                            <Grid item xs={3}>
                                                <Grid container gap={1} alignItems={"center"}>
                                                    <IconCircle size={6} htmlColor={theme.palette.greySecondary[500]} />
                                                    {t(`common::label::End date`, `End date`)}
                                                </Grid>
                                            </Grid>
                                            <Grid item>{`${moment(item.completedAt)
                                                .add(9, "h")
                                                .format("YYYY-MM-DD HH:mm:ss")}`}</Grid>
                                        </Grid>
                                    )}
                                </Stack>
                                <Collapse in={!!isExpanded[item.id]} collapsedSize={75}>
                                    <Box
                                        px={1}
                                        py={1.5}
                                        borderRadius={1}
                                        bgcolor={alpha(theme.palette.greySecondary[50], 0.5)}
                                        maxHeight={500}
                                        overflow={"auto"}
                                    >
                                        <Grid container gap={1.5}>
                                            <Grid item>
                                                <Tooltip
                                                    title={t(
                                                        `common::label::${isExpanded[item.id] ? "Fold" : "Expand"}`,
                                                        isExpanded[item.id] ? "Fold" : "Expand",
                                                    )}
                                                >
                                                    <IconButton
                                                        circled
                                                        size="xxsmall"
                                                        onClick={() => toggleJobIsExpanded(item)}
                                                        sx={{ width: 20, height: 20 }}
                                                    >
                                                        {!isExpanded[item.id] ? (
                                                            <IconArrowDown size={12} />
                                                        ) : (
                                                            <IconArrowUp size={12} />
                                                        )}
                                                    </IconButton>
                                                </Tooltip>
                                            </Grid>
                                            <Grid item xs>
                                                <Stack gap={1.5}>
                                                    <Grid container>
                                                        <Grid item xs>
                                                            <Grid container mt={0.25} mb={1.5} alignItems={"center"}>
                                                                <Trans
                                                                    i18nKey="common::msg::Detailed view of <0> {{number}} </0> tasks"
                                                                    values={{ number: item.inputFiles.length || 0 }}
                                                                    components={[
                                                                        <Typography
                                                                            variant={"body3_700"}
                                                                            color="primary.main"
                                                                        ></Typography>,
                                                                    ]}
                                                                />
                                                            </Grid>
                                                            <Grid container gap={0.75} alignItems={"center"}>
                                                                {renderJobStatus(item)}
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                </Stack>
                                                {isExpanded[item.id] && (
                                                    <Stack py={1.5} gap={2}>
                                                        <Box>
                                                            <Grid container flexWrap={"nowrap"}>
                                                                <Grid item>
                                                                    <ObjectIcon path={item.inputFiles[0].displayName} />
                                                                </Grid>
                                                                <Grid item>
                                                                    <GridNameEllipsis item maxWidth={"300px"}>
                                                                        <Typography variant="body3_400">
                                                                            {item.inputFiles[0].displayName ??
                                                                                item.inputFiles[0].name}
                                                                        </Typography>
                                                                    </GridNameEllipsis>
                                                                    {item.inputFiles.length > 1 && (
                                                                        <>
                                                                            <Typography variant="body3_400">
                                                                                <Trans
                                                                                    i18nKey="common::label::and <emp>{{count}}</emp> others"
                                                                                    components={{
                                                                                        emp: (
                                                                                            <Typography
                                                                                                variant="body3_700"
                                                                                                color="primary.main"
                                                                                            ></Typography>
                                                                                        ),
                                                                                    }}
                                                                                    values={{
                                                                                        count:
                                                                                            item.inputFiles.length - 1,
                                                                                    }}
                                                                                />
                                                                            </Typography>
                                                                        </>
                                                                    )}
                                                                </Grid>
                                                            </Grid>
                                                            <Box mt={2}>
                                                                {item.inputFiles.map((file, i) => (
                                                                    <Grid container flexWrap={"nowrap"} gap={1}>
                                                                        <Grid item>
                                                                            <IconHierarchyFolded
                                                                                size={6}
                                                                                htmlColor={
                                                                                    theme.palette.greySecondary[500]
                                                                                }
                                                                            />
                                                                        </Grid>
                                                                        <GridNameEllipsis item maxWidth={"300px"}>
                                                                            <Typography variant="body2_500">
                                                                                {file.displayName ?? file.name}{" "}
                                                                                {" - " + convertToByte(file.size)}
                                                                            </Typography>
                                                                        </GridNameEllipsis>
                                                                        {/*<Grid item>*/}
                                                                        {/*    <Tooltip*/}
                                                                        {/*        title={t(*/}
                                                                        {/*            `common::label::${"Transcoding error"}`,*/}
                                                                        {/*            `Transcoding error`,*/}
                                                                        {/*        )}*/}
                                                                        {/*    >*/}
                                                                        {/*        <IconButton circled size="small">*/}
                                                                        {/*            <IconErrorCircle*/}
                                                                        {/*                size={16}*/}
                                                                        {/*                color="error"*/}
                                                                        {/*            />*/}
                                                                        {/*        </IconButton>*/}
                                                                        {/*    </Tooltip>*/}
                                                                        {/*</Grid>*/}
                                                                    </Grid>
                                                                ))}
                                                            </Box>
                                                        </Box>
                                                    </Stack>
                                                )}
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </Collapse>
                            </Box>
                        ))}
                    </>
                ) : (
                    <JobEmptyList />
                )}
            </Stack>
        </Grid>
    );
};

export default TranscodeJobList;
